import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import useMutationRunner from "~/hooks/useMutationRunner";
import {
	useMarkAllNotificationsReadMutation,
	useMarkNotificationReadMutation,
	useMarkNotificationUnreadMutation
} from "~/services/endpoints/apiEndpoints";

export interface UseNotificationMutationsReturn {
	isSaving: boolean;
	markAllNotificationsRead: () => Promise<boolean>;
	markNotificationRead: (notificationId: string) => Promise<boolean>;
	markNotificationUnread: (notificationId: string) => Promise<boolean>;
}

const useNotificationMutations = (): UseNotificationMutationsReturn => {
	const { t } = useTranslation("common");
	const { run } = useMutationRunner();
	const [markReadMutation, { isLoading: isMarkingRead }] = useMarkNotificationReadMutation();
	const [markUnreadMutation, { isLoading: isMarkingUnread }] = useMarkNotificationUnreadMutation();
	const [markAllReadMutation, { isLoading: isMarkingAllRead }] = useMarkAllNotificationsReadMutation();

	const markNotificationRead = useCallback(
		(notificationId: string): Promise<boolean> =>
			run(
				markReadMutation({ input: { notificationId } }),
				(data) => data.markNotificationRead,
				t("notifications.markReadError"),
				{ noChangesSilentSuccess: true }
			),
		[markReadMutation, run, t]
	);

	const markNotificationUnread = useCallback(
		(notificationId: string): Promise<boolean> =>
			run(
				markUnreadMutation({ input: { notificationId } }),
				(data) => data.markNotificationUnread,
				t("notifications.markUnreadError"),
				{ noChangesSilentSuccess: true }
			),
		[markUnreadMutation, run, t]
	);

	const markAllNotificationsRead = useCallback(
		(): Promise<boolean> =>
			run(markAllReadMutation({}), (data) => data.markAllNotificationsRead, t("notifications.markAllReadError")),
		[markAllReadMutation, run, t]
	);

	return {
		isSaving: isMarkingRead || isMarkingUnread || isMarkingAllRead,
		markAllNotificationsRead,
		markNotificationRead,
		markNotificationUnread
	};
};

export default useNotificationMutations;
